import { dailyLearningApi, type DailyWorksheetDay } from '@mathflat/domain/DailyLearning'
import type { Student } from '@mathflat/domain/Student'
import { addDays, endOfWeek, isMonday as isMondayFn, startOfWeek } from 'date-fns'
import { makeAutoObservable, runInAction } from 'mobx'

import { getLearningDayStart } from '~/@common/utils/dailyLearningTime'

export default class DailyLearningService {
  dailyLearning: DailyWorksheetDay[] = []

  constructor() {
    makeAutoObservable(this)
  }

  get isAllNotStarted() {
    return this.dailyLearning.every((day) => day.status === 'NOT_STARTED')
  }

  // 학습일 기준은 자정이 아니라 오전 2시부터 시작, 사용자가 오전 2시 이전에 한 학습은 전날 학습으로 간주
  get todayLearning() {
    const now = new Date()
    return this.dailyLearning.find((day) => {
      const start = getLearningDayStart(day.date)
      return now >= start && now < addDays(start, 1)
    })
  }

  isToday(day: DailyWorksheetDay): boolean {
    const now = new Date()
    const start = getLearningDayStart(day.date)
    return now >= start && now < addDays(start, 1)
  }

  isPast(day: DailyWorksheetDay): boolean {
    const now = new Date()
    return now >= addDays(getLearningDayStart(day.date), 1)
  }

  get consecutiveDailyLearningCount() {
    const todayItem = this.todayLearning
    if (!todayItem) return 0

    const todayIndex = this.dailyLearning.findIndex((day) => day.date === todayItem.date)
    if (todayIndex === -1) return 0

    let count = 0
    // 오늘부터 이전으로 거슬러 올라가면서 연속된 COMPLETED 또는 IN_PROGRESS 개수 세기
    for (let i = todayIndex; i >= 0; i--) {
      const item = this.dailyLearning[i]
      if (item.status === 'COMPLETED' || item.status === 'IN_PROGRESS') {
        count++
      } else {
        break // 연속이 끊기면 중단
      }
    }
    return count
  }

  // 데이터트래킹 용 - 최대 연속 학습 일자
  get maxConsecutiveWeeklyLearningCount() {
    // 현재 주의 월요일부터 일요일까지 데이터 필터링
    const today = new Date()
    const weekStart = startOfWeek(today, { weekStartsOn: 1 }) // 월요일 시작
    const weekEnd = endOfWeek(today, { weekStartsOn: 1 }) // 일요일 끝

    const currentWeekItems = this.dailyLearning
      .filter((item) => {
        const itemDate = new Date(item.date)
        return itemDate >= weekStart && itemDate <= weekEnd
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()) // 날짜 순 정렬

    if (currentWeekItems.length === 0) return 0

    // 주 내에서 가장 긴 연속 학습 기간 찾기
    let maxCount = 0
    let currentCount = 0

    for (const item of currentWeekItems) {
      if (item.status === 'COMPLETED' || item.status === 'IN_PROGRESS') {
        currentCount++
        maxCount = Math.max(maxCount, currentCount)
      } else {
        currentCount = 0
      }
    }

    return maxCount
  }

  get isConsecutiveDailyLearning() {
    return this.consecutiveDailyLearningCount > 1
  }

  get isMonday() {
    return isMondayFn(new Date())
  }

  // 월요일부터 오늘까지 연속 달성 현황 (새벽 2시 기준 오늘 기준)
  // count: 월요일부터 연속으로 COMPLETED된 일수 (오늘 COMPLETED 시 포함)
  // broken: 월요일~어제 사이에 미달성일이 있는 경우 true
  get mondayStreakInfo(): { count: number; broken: boolean } {
    const todayItem = this.todayLearning
    if (!todayItem) return { count: 0, broken: false }

    const mondayIndex = this.dailyLearning.findIndex((d) => d.day === 'MONDAY')
    if (mondayIndex === -1) return { count: 0, broken: false }

    const todayIndex = this.dailyLearning.findIndex((d) => d.date === todayItem.date)
    if (todayIndex < mondayIndex) return { count: 0, broken: false }

    let count = 0
    let broken = false

    for (let i = mondayIndex; i <= todayIndex; i++) {
      const day = this.dailyLearning[i]
      if (day.status === 'COMPLETED') {
        count++
      } else if (i < todayIndex) {
        broken = true
        count = 0
        break
      }
      // 오늘이 미완료인 경우 카운트 안 함 (streak는 유지)
    }

    return { count, broken }
  }

  async loadDailyLearning(studentId: string, dateRange?: { startDate: string; endDate: string }) {
    const data = await dailyLearningApi.getDailyWorksheetDays(studentId as Student['id'], dateRange)
    runInAction(() => {
      this.dailyLearning = data
    })
  }

  async startDailyLearning() {
    const studentWorksheetId = await dailyLearningApi.createDailyWorksheet()
    return studentWorksheetId
  }
}
